import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import { Tag } from 'primereact/tag';
import { Card } from 'primereact/card';
import { Badge } from 'primereact/badge';
import api from '../../services/api';
import MobileAvatar from './MobileAvatar';

export default function MobileUsersList() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const toast = useRef(null);
  const navigate = useNavigate();

  const fetchUsers = async (query = q) => {
    try {
      setLoading(true);
      const { data } = await api.get('/mobile_users', { params: { q: query, limit: 100 } });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load users' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Card>
      <div className="card p-4">
        <Toast ref={toast} />
        <div className="grid mb-4">
          <div className="col-6 flex align-items-center">
            <h2 className="m-0 main-text font-bold vertical-align-middle">
              <span>Mobile users</span>
              <Badge
                className="ml-2 b-4 relative"
                value={total ?? 0}
                style={{ bottom: '3px', backgroundColor: '#2020A0' }}
              />
            </h2>
          </div>
        </div>

        <div className="grid mb-4">
          <div className="col-12 flex gap-2 align-items-end flex-wrap">
            <div className="flex flex-column" style={{ minWidth: 250 }}>
              <label style={{ fontSize: '0.95rem' }}>
                <strong>Search</strong>
              </label>
              <InputText
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && fetchUsers()}
                className="w-full p-button-sm"
                placeholder="Search email / name"
                style={{ fontSize: '0.95rem' }}
              />
            </div>
            <Button
              label="Search"
              icon="pi pi-search"
              className="p-button-primary p-button-sm secondary-button"
              onClick={() => fetchUsers()}
            />
            <Button
              label="Clear"
              icon="pi pi-times"
              className="p-button-text text-danger p-button-sm"
              onClick={() => {
                setQ('');
                fetchUsers('');
              }}
            />
          </div>
        </div>

        {loading && !items.length ? (
          <div
            className="flex flex-column align-items-center justify-content-center"
            style={{ minHeight: '300px' }}
          >
            <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem', color: '#2020A0' }} />
            <div style={{ marginTop: '1rem', fontSize: '1.2rem', color: '#555' }}>
              Loading mobile users...
            </div>
          </div>
        ) : (
          <DataTable
            value={items}
            paginator
            rows={25}
            loading={loading}
            emptyMessage="No mobile users found."
            className="p-datatable-sm"
            showGridlines
            stripedRows
          >
            <Column
              field="displayName"
              header="Name"
              sortable
              body={(row) => (
                <div className="flex align-items-center gap-2">
                  <MobileAvatar
                    avatar={row.avatar}
                    name={row.displayName}
                    photoUrl={row.photoUrl}
                    size={36}
                  />
                  <span>{row.displayName || '—'}</span>
                </div>
              )}
            />
            <Column field="communityName" header="Community name" sortable />
            <Column field="email" header="Email" sortable />
            <Column
              field="provider"
              header="Provider"
              body={(row) => <Tag value={row.provider || 'email'} />}
            />
            <Column field="level" header="Level" />
            <Column field="points" header="Points" sortable />
            <Column field="totalReports" header="Reports" />
            <Column field="totalReactions" header="Reactions" />
            <Column
              header="View"
              body={(row) => (
                <Button
                  icon="pi pi-eye"
                  className="p-button-text p-button-sm"
                  onClick={() => navigate(`/mobile_users/${row._id}`)}
                  tooltip="View user"
                  tooltipOptions={{ position: 'top' }}
                />
              )}
              style={{ width: '6rem', textAlign: 'center' }}
            />
          </DataTable>
        )}
      </div>
    </Card>
  );
}
